import React, { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, RefreshCw, TrendingUp, Users, BarChart2 } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminDashboard() {
  const [stats, setStats] = useState({ totalDeposit: 0, totalWithdraw: 0, totalUsers: 0 });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [period, setPeriod] = useState("today");
  const [customDate, setCustomDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  });
  const [depFilter, setDepFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    loadStats();
    loadReport("today");
    loadUsers("today", null, "all");
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/stats");
      setStats((s) => ({ ...s, ...(res.data || {}) }));
    } catch (err) { console.log(err); }
    finally { setLoading(false); }
  };

  const loadReport = async (p, d) => {
    setReportLoading(true);
    try {
      const params = { period: p };
      if (p === "date" && d) params.date = d;
      const res = await API.get("/admin/daily-report", { params });
      setReport(res.data);
    } catch (err) { console.log(err); }
    finally { setReportLoading(false); }
  };

  const loadUsers = async (p, d, df) => {
    setUsersLoading(true);
    try {
      const params = { period: p, deposit_filter: df };
      if (p === "date" && d) params.date = d;
      const res = await API.get("/admin/daily-report/users", { params });
      setUsers(res.data.users || []);
    } catch (err) { console.log(err); }
    finally { setUsersLoading(false); }
  };

  const handlePeriod = (p) => {
    setPeriod(p);
    if (p !== "date") { loadReport(p); loadUsers(p, null, depFilter); }
  };

  const handleDateChange = (val) => {
    setCustomDate(val);
    if (val) { loadReport("date", val); loadUsers("date", val, depFilter); }
  };

  const handleDepFilter = (df) => {
    setDepFilter(df);
    const d = period === "date" ? customDate : null;
    loadUsers(period, d, df);
  };

  const fmt = (v) => {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const profit = Number(stats.totalDeposit || 0) - Number(stats.totalWithdraw || 0);
  const rProfit = report ? (report.deposits.total - report.withdrawals.total) : 0;
  const periodLabel = { today: "Today", week: "This Week", month: "This Month", date: customDate }[period] || "";

  const withDep = users.filter(u => Number(u.dep_total) > 0).length;
  const withoutDep = users.filter(u => Number(u.dep_total) === 0).length;

  return (
    <AdminLayout>
      <div style={wrap}>
        <div style={pageHeader}>
          <div>
            <h1 style={title}>Dashboard</h1>
            <p style={subtitle}>Platform overview & analytics</p>
          </div>
          <button style={refreshBtn} onClick={() => { loadStats(); const d = period === "date" ? customDate : null; loadReport(period, d); loadUsers(period, d, depFilter); }} disabled={loading || reportLoading || usersLoading} title="Refresh">
            <RefreshCw size={16} color="#6366f1" style={(loading || reportLoading || usersLoading) ? { animation: "spin 1s linear infinite" } : {}} />
          </button>
        </div>

        <div style={grid}>
          <StatCard icon={<ArrowUpCircle size={26} />} label="Total Deposits" value={fmt(stats.totalDeposit)} from="#22c55e" to="#16a34a" emoji="💰" />
          <StatCard icon={<ArrowDownCircle size={26} />} label="Total Withdrawals" value={fmt(stats.totalWithdraw)} from="#ef4444" to="#dc2626" emoji="📤" />
          <StatCard icon={<TrendingUp size={26} />} label="Net Profit" value={fmt(profit)} from="#6366f1" to="#4f46e5" emoji="📈" />
          <StatCard icon={<Users size={26} />} label="Total Users" value={Number(stats.totalUsers || 0).toLocaleString("en-IN")} from="#f97316" to="#ea580c" emoji="👥" />
        </div>

        {/* ── Daily Report ── */}
        <div style={reportSection}>
          <div style={reportHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart2 size={20} color="#6366f1" />
              <span style={reportTitle}>Daily Report</span>
              {report && <span style={periodBadge}>{periodLabel}</span>}
              {(reportLoading || usersLoading) && <span style={loadingDot}>•••</span>}
            </div>
            <div style={filterRow}>
              {["today","week","month","date"].map((p) => (
                <button key={p} style={period === p ? filterBtnActive : filterBtn} onClick={() => handlePeriod(p)}>
                  {p === "today" ? "Today" : p === "week" ? "This Week" : p === "month" ? "This Month" : "Pick Date"}
                </button>
              ))}
              {period === "date" && (
                <input type="date" value={customDate} onChange={(e) => handleDateChange(e.target.value)} style={dateInput} />
              )}
            </div>
          </div>

          <div style={reportGrid}>
            <ReportCard label="Deposits" emoji="💰" count={report ? report.deposits.count : "—"} value={report ? fmt(report.deposits.total) : "—"} from="#22c55e" to="#16a34a" sub="approved deposits" />
            <ReportCard label="Withdrawals" emoji="📤" count={report ? report.withdrawals.count : "—"} value={report ? fmt(report.withdrawals.total) : "—"} from="#ef4444" to="#dc2626" sub="approved payouts" />
            <ReportCard label="Net Profit" emoji="📈" count={null} value={report ? fmt(rProfit) : "—"} from={rProfit >= 0 ? "#6366f1" : "#9f1239"} to={rProfit >= 0 ? "#4f46e5" : "#881337"} sub="deposits − withdrawals" />
            <ReportCard label="New Users" emoji="👤" count={null} value={report ? String(report.newUsers) : "—"} from="#f97316" to="#ea580c" sub="registered in period" />
          </div>
        </div>

        {/* ── Registered Users List ── */}
        <div style={reportSection}>
          <div style={reportHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={20} color="#f97316" />
              <span style={reportTitle}>Registered Users</span>
              <span style={{ ...periodBadge, background: "#fff7ed", color: "#f97316" }}>{periodLabel}</span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>Total: {users.length}</span>
            </div>
            <div style={filterRow}>
              <button style={depFilter === "all" ? { ...filterBtnActive, background: "#6366f1" } : filterBtn} onClick={() => handleDepFilter("all")}>
                All ({depFilter === "all" ? users.length : withDep + withoutDep})
              </button>
              <button style={depFilter === "with" ? { ...filterBtnActive, background: "#22c55e", border: "1px solid #22c55e" } : { ...filterBtn, color: "#16a34a", borderColor: "#bbf7d0" }} onClick={() => handleDepFilter("with")}>
                💰 With Deposit
              </button>
              <button style={depFilter === "without" ? { ...filterBtnActive, background: "#ef4444", border: "1px solid #ef4444" } : { ...filterBtn, color: "#dc2626", borderColor: "#fecaca" }} onClick={() => handleDepFilter("without")}>
                🚫 No Deposit
              </button>
            </div>
          </div>

          {usersLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 14 }}>Loading users…</div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 14 }}>No users registered in this period</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Name</th>
                    <th style={th}>Phone</th>
                    <th style={th}>Registered</th>
                    <th style={th}>Wallet</th>
                    <th style={th}>Deposit</th>
                    <th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => {
                    const hasDep = Number(u.dep_total) > 0;
                    const regTime = u.created_at ? new Date(u.created_at).toLocaleString("en-IN", { hour12: true, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                    return (
                      <tr key={u.id} style={trHover} onClick={() => window.open(`/admin/users/${u.id}`, "_blank")}>
                        <td style={td}>{u.id}</td>
                        <td style={{ ...td, fontWeight: 700, color: "#1e293b" }}>{u.name || "—"}</td>
                        <td style={td}>{u.phone || u.email || "—"}</td>
                        <td style={{ ...td, color: "#64748b", fontSize: 12 }}>{regTime}</td>
                        <td style={td}>₹{Number(u.balance).toLocaleString("en-IN")}</td>
                        <td style={{ ...td, fontWeight: 700, color: hasDep ? "#16a34a" : "#94a3b8" }}>
                          {hasDep ? fmt(u.dep_total) : "₹0"}
                        </td>
                        <td style={td}>
                          <span style={hasDep ? badgeGreen : badgeGray}>
                            {hasDep ? `${u.dep_count} deposit${u.dep_count > 1 ? "s" : ""}` : "No deposit"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={quickGrid}>
          <QuickLink label="Deposits" emoji="💵" sub="Pending approvals" path="/admin/deposits" color="#22c55e" />
          <QuickLink label="Withdrawals" emoji="📤" sub="Pending payouts" path="/admin/payouts" color="#ef4444" />
          <QuickLink label="Users" emoji="👤" sub="Manage players" path="/admin/users" color="#6366f1" />
          <QuickLink label="Results" emoji="🏆" sub="Declare results" path="/admin/results" color="#f59e0b" />
          <QuickLink label="Starline" emoji="⭐" sub="Starline control" path="/admin/starline" color="#8b5cf6" />
          <QuickLink label="Settings" emoji="⚙️" sub="App configuration" path="/admin/settings" color="#0ea5e9" />
        </div>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .user-row:hover { background: #f8fafc !important; }
      `}</style>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, from, to, emoji }) {
  return (
    <div style={{ ...card, background: `linear-gradient(135deg, ${from}, ${to})` }}>
      <div style={cardEmoji}>{emoji}</div>
      <div style={cardBody}>
        <p style={cardLabel}>{label}</p>
        <p style={cardValue}>{value}</p>
      </div>
      <div style={cardIconWrap}>{React.cloneElement(icon, { color: "rgba(255,255,255,0.3)" })}</div>
    </div>
  );
}

function ReportCard({ label, emoji, count, value, from, to, sub }) {
  return (
    <div style={{ ...rCard, background: `linear-gradient(135deg, ${from}, ${to})` }}>
      <div style={{ fontSize: 28, flexShrink: 0 }}>{emoji}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 900, margin: "4px 0 2px", letterSpacing: "-0.5px" }}>{value}</div>
        {count !== null && <div style={{ fontSize: 11, opacity: 0.75 }}>{count} {sub}</div>}
        {count === null && <div style={{ fontSize: 11, opacity: 0.75 }}>{sub}</div>}
      </div>
    </div>
  );
}

function QuickLink({ label, emoji, sub, path, color }) {
  return (
    <a href={path} style={{ ...quickCard, borderTop: `3px solid ${color}` }}>
      <span style={quickEmoji}>{emoji}</span>
      <div>
        <div style={quickLabel}>{label}</div>
        <div style={quickSub}>{sub}</div>
      </div>
    </a>
  );
}

const wrap = { padding: "28px 24px", maxWidth: 1100, margin: "0 auto" };
const pageHeader = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 };
const title = { margin: 0, fontSize: 24, fontWeight: 900, color: "#1e1b4b" };
const subtitle = { margin: "4px 0 0", fontSize: 13, color: "#6b7280" };
const refreshBtn = { width: 40, height: 40, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, display: "grid", placeItems: "center", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 };
const card = { borderRadius: 20, padding: "22px 20px", display: "flex", alignItems: "center", gap: 14, color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" };
const cardEmoji = { fontSize: 32, flexShrink: 0 };
const cardBody = { flex: 1 };
const cardLabel = { margin: 0, fontSize: 12, fontWeight: 600, opacity: 0.85 };
const cardValue = { margin: "6px 0 0", fontSize: 26, fontWeight: 900, letterSpacing: "-0.5px" };
const cardIconWrap = { position: "absolute", right: 16, bottom: 12 };

const reportSection = { background: "#fff", borderRadius: 20, padding: "22px 20px", marginBottom: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9" };
const reportHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 };
const reportTitle = { fontSize: 16, fontWeight: 800, color: "#1e1b4b" };
const periodBadge = { background: "#ede9fe", color: "#6366f1", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 };
const loadingDot = { color: "#94a3b8", fontSize: 18, letterSpacing: 2 };
const filterRow = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const filterBtn = { padding: "6px 14px", borderRadius: 20, border: "1px solid #e5e7eb", background: "#f8fafc", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const filterBtnActive = { ...filterBtn, background: "#6366f1", color: "#fff", border: "1px solid #6366f1" };
const dateInput = { padding: "5px 10px", borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12, color: "#374151", background: "#f9fafb", cursor: "pointer" };
const reportGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 };
const rCard = { borderRadius: 16, padding: "18px 16px", display: "flex", alignItems: "center", gap: 14, color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.10)" };

const tbl = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #f1f5f9", background: "#fafafa" };
const td = { padding: "11px 12px", borderBottom: "1px solid #f1f5f9", color: "#374151", verticalAlign: "middle" };
const trHover = { cursor: "pointer", transition: "background 0.15s" };
const badgeGreen = { background: "#dcfce7", color: "#16a34a", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" };
const badgeGray = { background: "#f1f5f9", color: "#94a3b8", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" };

const quickGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 };
const quickCard = { background: "#fff", borderRadius: 16, padding: "18px 16px", display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "#1e293b", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9" };
const quickEmoji = { fontSize: 28 };
const quickLabel = { fontSize: 14, fontWeight: 800, color: "#1e293b" };
const quickSub = { fontSize: 11, color: "#94a3b8", marginTop: 2 };

export default AdminDashboard;
